import { Checkbox, Icon, IconButton, Text } from '@nike/eds'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react'
import { useFetchAvailableChannelsQuery } from 'api/config'
import { useFetchCountriesQuery } from 'api/country'
import {
  RASN_CHANNELS,
  SHIPTO_REGEX,
  type FilterCondition
} from 'types/Instruction'
import { RasnChannel } from 'types/RASN'

const LOCKED_HINT_SHIPTO = 'Locked — clear Country to enable'
const LOCKED_HINT_COUNTRY = 'Locked — clear Ship To to enable'

// ----------------------- shared dropdown helper -----------------------

const useDropdown = (initialOpen = false) => {
  const [isOpen, setIsOpen] = useState(initialOpen)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const onMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('mousedown', onMouseDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [isOpen])

  return { isOpen, setIsOpen, wrapperRef }
}

const useDebouncedValue = <T,>(value: T, delay: number): T => {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const id = setTimeout(() => { setDebounced(value) }, delay)
    return () => { clearTimeout(id) }
  }, [value, delay])
  return debounced
}

// ----------------------- ChannelMultiSelect -----------------------

interface ChannelMultiSelectProps {
  value: RasnChannel[]
  onChange: (next: RasnChannel[]) => void
  disabled?: boolean
}

const ChannelMultiSelect = memo(({ value, onChange, disabled }: ChannelMultiSelectProps) => {
  const { isOpen, setIsOpen, wrapperRef } = useDropdown()
  const { data: availableChannels } = useFetchAvailableChannelsQuery()

  // Channels shown in the dropdown. Defaults to the full enum so older
  // backends that don't yet serve /config/channels keep the existing UX.
  // Already-selected values not in the available list (e.g. an SI imported
  // from a DC that exposes RETAIL/WHOLESALE) stay visible so supervisors
  // can still deselect them.
  const channels = useMemo(() => {
    const base = availableChannels ?? RASN_CHANNELS
    const merged = [...base]
    for (const ch of value) {
      if (!merged.includes(ch)) merged.push(ch)
    }
    return merged
  }, [availableChannels, value])

  const toggle = useCallback((ch: RasnChannel) => {
    if (value.includes(ch)) {
      onChange(value.filter(c => c !== ch))
    } else {
      onChange([...value, ch])
    }
  }, [value, onChange])

  const triggerLabel = value.length === 0 ? 'Select channels' : value.join(', ')

  return (
    <div ref={wrapperRef} className="fc-dropdown">
      <label className="fc-label">Channel</label>
      <button
        type="button"
        className={`fc-trigger ${value.length === 0 ? 'fc-trigger--placeholder' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(o => !o) }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate">{triggerLabel}</span>
        <Icon name={isOpen ? 'CaretUp' : 'CaretDown'} size="s" />
      </button>
      {isOpen && (
        <div className="fc-panel" role="listbox">
          {channels.map(ch => (
            <label key={ch} className="fc-option">
              <Checkbox
                id={`channel-${ch}`}
                label={ch}
                checked={value.includes(ch)}
                onChange={() => { toggle(ch) }}
              />
            </label>
          ))}
        </div>
      )}
    </div>
  )
})
ChannelMultiSelect.displayName = 'ChannelMultiSelect'

// ----------------------- ShipToTagInput -----------------------

interface ShipToTagInputProps {
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  lockedHint?: string | null
}

const ShipToTagInput = memo(({ value, onChange, disabled, lockedHint }: ShipToTagInputProps) => {
  const [draft, setDraft] = useState('')
  // Surfaced inline the moment the operator hits Enter/comma/paste with a value
  // that fails SHIPTO_REGEX, so they don't have to round-trip through Save to
  // see the rule. Cleared as soon as they edit the draft.
  const [inputError, setInputError] = useState<string | null>(null)

  // Splits the raw input into valid and invalid tokens. Valid ones are merged
  // into `value`; invalid ones are reported inline and returned so the caller
  // can leave them in the draft for the operator to fix.
  const addTokens = useCallback((raw: string): string => {
    const tokens = raw.split(',').map(t => t.trim()).filter(t => t.length > 0)
    if (tokens.length === 0) return ''
    const valid: string[] = []
    const invalid: string[] = []
    for (const t of tokens) {
      if (SHIPTO_REGEX.test(t)) valid.push(t)
      else invalid.push(t)
    }
    if (valid.length > 0) {
      const merged = [...value]
      for (const t of valid) {
        if (!merged.includes(t)) merged.push(t)
      }
      if (merged.length !== value.length) onChange(merged)
    }
    if (invalid.length > 0) {
      setInputError(`"${invalid.join('", "')}" is not a valid ShipTo — use letters, digits, or - _ / (e.g. ABC1067)`)
    } else {
      setInputError(null)
    }
    return invalid.join(', ')
  }, [value, onChange])

  const onKeyDown = useCallback((e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      if (draft.trim()) {
        const leftover = addTokens(draft)
        setDraft(leftover)
      }
    } else if (e.key === 'Backspace' && draft === '' && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }, [draft, value, addTokens, onChange])

  const onPaste = useCallback((e: ClipboardEvent<HTMLInputElement>) => {
    const text = e.clipboardData.getData('text')
    if (text.includes(',')) {
      e.preventDefault()
      const leftover = addTokens(text)
      setDraft(leftover)
    }
  }, [addTokens])

  const remove = useCallback((tag: string) => {
    onChange(value.filter(v => v !== tag))
  }, [value, onChange])

  return (
    <div className="fc-dropdown">
      <label className="fc-label">Ship To</label>
      <div className={`fc-tag-field ${disabled ? 'fc-tag-field--disabled' : ''}`}>
        {value.map(tag => (
          <span key={tag} className="fc-chip">
            {tag}
            {!disabled && (
              <button
                type="button"
                className="fc-chip__close"
                aria-label={`Remove ${tag}`}
                onClick={() => { remove(tag) }}
              >×</button>
            )}
          </span>
        ))}
        <input
          type="text"
          className="fc-tag-input"
          placeholder={value.length === 0 ? (disabled && lockedHint ? '' : 'Type or scan a ShipTo, press Enter to add') : ''}
          value={draft}
          disabled={disabled}
          onChange={(e) => {
            setDraft(e.target.value)
            if (inputError) setInputError(null)
          }}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
      </div>
      <div className="fc-help">{lockedHint ?? `${value.length} selected`}</div>
      {inputError && <Text font="legal-1" color="text-danger">{inputError}</Text>}
    </div>
  )
})
ShipToTagInput.displayName = 'ShipToTagInput'

// ----------------------- CountryMultiSelect -----------------------

interface CountryMultiSelectProps {
  value: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
  lockedHint?: string | null
}

const CountryMultiSelect = memo(({ value, onChange, disabled, lockedHint }: CountryMultiSelectProps) => {
  const { isOpen, setIsOpen, wrapperRef } = useDropdown()
  const { data: countries = [] } = useFetchCountriesQuery()

  const sorted = useMemo(
    () => [...countries].sort((a, b) => a.countryCode.localeCompare(b.countryCode)),
    [countries]
  )

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebouncedValue(search.trim().toUpperCase(), 200)
  const filtered = useMemo(() => {
    if (!debouncedSearch) return sorted
    return sorted.filter(c =>
      c.countryCode.includes(debouncedSearch) ||
      c.fullName.toUpperCase().includes(debouncedSearch)
    )
  }, [sorted, debouncedSearch])

  const toggle = useCallback((code: string) => {
    if (value.includes(code)) {
      onChange(value.filter(v => v !== code))
    } else {
      onChange([...value, code])
    }
  }, [value, onChange])

  const triggerLabel = value.length === 0 ? 'Select countries' : value.join(', ')

  return (
    <div ref={wrapperRef} className="fc-dropdown">
      <label className="fc-label">Country</label>
      <button
        type="button"
        className={`fc-trigger ${value.length === 0 ? 'fc-trigger--placeholder' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(o => !o) }}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate">{triggerLabel}</span>
        <Icon name={isOpen ? 'CaretUp' : 'CaretDown'} size="s" />
      </button>
      {lockedHint && <div className="fc-help">{lockedHint}</div>}
      {isOpen && (
        <div className="fc-panel fc-panel--country" role="listbox">
          <input
            type="text"
            className="fc-search"
            placeholder="Search countries..."
            value={search}
            onChange={(e) => { setSearch(e.target.value) }}
            autoFocus
          />
          <div className="fc-options">
            {filtered.map(c => (
              <label key={c.countryCode} className="fc-option fc-option--row">
                <Checkbox
                  id={`country-${c.countryCode}`}
                  label={`${c.countryCode} — ${c.fullName}`}
                  checked={value.includes(c.countryCode)}
                  onChange={() => { toggle(c.countryCode) }}
                />
              </label>
            ))}
            {filtered.length === 0 && (
              <div className="fc-empty">No matches</div>
            )}
          </div>
          <div className="fc-footer">
            <span>{value.length} of {sorted.length} selected</span>
            <button
              type="button"
              className="fc-link"
              onClick={() => { onChange([]) }}
            >Clear</button>
          </div>
        </div>
      )}
    </div>
  )
})
CountryMultiSelect.displayName = 'CountryMultiSelect'

// ----------------------- FilterConditionRow -----------------------

interface FilterConditionRowProps {
  value: FilterCondition
  onChange: (next: FilterCondition) => void
  onRemove: () => void
  disabled?: boolean
  error?: string | null
}

export const FilterConditionRow = memo(({ value, onChange, onRemove, disabled, error }: FilterConditionRowProps) => {
  const shipToLocked = value.countries.length > 0
  const countryLocked = value.shipTos.length > 0

  const onChannels = useCallback((channels: RasnChannel[]) => {
    onChange({ ...value, channels })
  }, [value, onChange])

  const onShipTos = useCallback((shipTos: string[]) => {
    onChange({ ...value, shipTos })
  }, [value, onChange])

  const onCountries = useCallback((countries: string[]) => {
    onChange({ ...value, countries })
  }, [value, onChange])

  return (
    <div className="filter-condition-row">
      <div className="fc-col fc-col--channel">
        <ChannelMultiSelect value={value.channels} onChange={onChannels} disabled={disabled} />
      </div>
      <div className="fc-col fc-col--shipto">
        <ShipToTagInput
          value={value.shipTos}
          onChange={onShipTos}
          disabled={disabled || shipToLocked}
          lockedHint={shipToLocked ? LOCKED_HINT_SHIPTO : null}
        />
      </div>
      <div className="fc-col fc-col--country">
        <CountryMultiSelect
          value={value.countries}
          onChange={onCountries}
          disabled={disabled || countryLocked}
          lockedHint={countryLocked ? LOCKED_HINT_COUNTRY : null}
        />
        {error && <Text font="legal-1" color="text-danger">{error}</Text>}
      </div>
      <div className="fc-col fc-col--actions">
        {!disabled && (
          <IconButton
            icon="Delete"
            label="Remove filter condition"
            variant="ghost"
            size="medium"
            onClick={onRemove}
          />
        )}
      </div>
    </div>
  )
})
FilterConditionRow.displayName = 'FilterConditionRow'
